import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, MapPin, ChevronDown, Loader, X, Palette ,Layers} from 'lucide-react';
import mapboxgl from 'mapbox-gl';
import { Select, Button, Typography, Space, Divider, ConfigProvider } from 'antd';
const { Title, Text } = Typography;

import 'mapbox-gl/dist/mapbox-gl.css';
import { fetchDistricts, fetchCompaniesByCity, fetchCompanies, fetchCompaniesByIndustry } from '../../api/api';
import { fetchIndustries } from "../../api/industryApi";

const VidharbhaIndustrialMap = () => {
  // --- State Management ---
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [districts, setDistricts] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedDistrictDetails, setSelectedDistrictDetails] = useState(null);
  const [mapStyle, setMapStyle] = useState('light-v11'); // NEW: Map style state

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  const mapboxToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

  // NEW: Map style options
  const mapStyles = [
    { value: 'light-v11', label: 'Light', description: 'Clean & minimal' },
    { value: 'dark-v11', label: 'Dark', description: 'Night mode' },
    { value: 'streets-v12', label: 'Streets', description: 'Detailed streets' },
    { value: 'outdoors-v12', label: 'Outdoors', description: 'Terrain view' },
    { value: 'satellite-v9', label: 'Satellite', description: 'Aerial imagery' },
    { value: 'satellite-streets-v12', label: 'Hybrid', description: 'Satellite + labels' },
    { value: 'navigation-day-v1', label: 'Navigation', description: 'GPS style' }
  ];

  // --- Initial Data Loading ---
  useEffect(() => {
    const initData = async () => {
      const [distRes, indRes] = await Promise.all([fetchDistricts(), fetchIndustries()]);
      if (distRes.success) {
        setDistricts(distRes.data.map(d => ({
          id: d._id,
          name: d.districtName,
          lat: d.latitude || 21.1458,
          lng: d.longitude || 79.0882
        })));
      }
      if (indRes?.success) setIndustries(indRes.data);
    };
    initData();
  }, []);

  // --- Map Initialization ---
  useEffect(() => {
    if (!mapboxToken || mapRef.current) return;
    mapboxgl.accessToken = mapboxToken;
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: `mapbox://styles/mapbox/${mapStyle}`,
      center: [79.0882, 21.1458], 
      zoom: 7,
      attributionControl: false
    });
    mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
  }, [mapboxToken]);

  // NEW: Handle map style change
  const handleMapStyleChange = (newStyle) => {
    setMapStyle(newStyle);
    if (mapRef.current) {
      mapRef.current.setStyle(`mapbox://styles/mapbox/${newStyle}`);
      
      // Re-add markers after style loads
      mapRef.current.once('style.load', () => {
        // Markers will be re-rendered by the existing useEffect
      });
    }
  };

  // --- Fetch and Plot Companies by District ---
  // --- Enhanced Coordinate-Based Selection Logic ---
  const handleDistrictSelection = async (districtId) => {
    // 1. Reset Logic: Returns to default Vidarbha coordinates
    if (!districtId) {
      setSelectedDistrict(null);
      setSelectedDistrictDetails(null);
      setCompanies([]);
      if (mapRef.current) {
        mapRef.current.flyTo({ 
          center: [79.0882, 21.1458], // [lng, lat]
          zoom: 7,
          duration: 1500 
        });
      }
      return;
    }

    const district = districts.find(d => d.id === districtId);
    setSelectedDistrict(districtId);
    setSelectedDistrictDetails(district);
    setCompaniesLoading(true);

    try {
      // 2. Fetch companies for the specific district
      const response = await fetchCompaniesByCity(district.name);
      
      if (response.success && response.data) {
        // 3. Coordinate Integrity Check: Filter out invalid [0,0] coordinates
        const validCompanies = response.data.filter(company => 
          company.location?.coordinates?.length === 2 &&
          company.location.coordinates[0] !== 0 &&
          company.location.coordinates[1] !== 0
        );
console.log("validCompanies", validCompanies);
        setCompanies(response.data);

        if (validCompanies.length > 0 && mapRef.current) {
          // 4. Dynamic Bounding Box: Calculate area based on company coordinates
          const bounds = new mapboxgl.LngLatBounds();
          validCompanies.forEach(company => {
            // Mapbox Requirement: Must be [longitude, latitude]
            bounds.extend(company.location.coordinates);
          });
          
          // 5. Dynamic Camera Adjustment: Centers and zooms to the specific geographic region
          mapRef.current.fitBounds(bounds, {
            padding: 100, // Enterprise-grade spacing
            maxZoom: 12,
            duration: 1500,
            essential: true
          });
        } else if (mapRef.current) {
          // Fallback: Zoom to the district's saved central coordinates
          mapRef.current.flyTo({ 
            center: [district.lng, district.lat], 
            zoom: 11,
            duration: 1500 
          });
        }
      }
    } catch (err) {
      console.error("Coordinate Mapping Error:", err);
      setCompanies([]);
    } finally {
      setCompaniesLoading(false);
    }
  };

  // --- Fetch Companies by Industry ---
  const handleIndustrySelection = async (industryId) => {
    // Reset industry filter
    if (!industryId) {
      setSelectedIndustry('');
      // If no district is selected, load all companies
      if (!selectedDistrict) {
        loadAllCompanies();
      } else {
        // If district is selected, reload district companies
        const district = districts.find(d => d.id === selectedDistrict);
        if (district) {
          loadCompaniesByDistrict(district);
        }
      }
      return;
    }

    const industry = industries.find(i => i._id === industryId);
    setSelectedIndustry(industryId);
    setCompaniesLoading(true);

    try {
      // If district is also selected, filter by both
      if (selectedDistrict) {
        const district = districts.find(d => d.id === selectedDistrict);
        const [districtRes, industryRes] = await Promise.all([
          fetchCompaniesByCity(district.name),
          fetchCompaniesByIndustry(industry.sectorName)
        ]);

        if (districtRes.success && industryRes.success) {
          // Find intersection of both results
          const districtCompanyIds = new Set(districtRes.data.map(c => c._id));
          const filteredCompanies = industryRes.data.filter(c => 
            districtCompanyIds.has(c._id) &&
            c.location?.coordinates?.length === 2 &&
            c.location.coordinates[0] !== 0
          );
          setCompanies(filteredCompanies);

          if (filteredCompanies.length > 0 && mapRef.current) {
            const bounds = new mapboxgl.LngLatBounds();
            filteredCompanies.forEach(company => {
              bounds.extend(company.location.coordinates);
            });
            mapRef.current.fitBounds(bounds, {
              padding: 100,
              maxZoom: 12,
              duration: 1500,
              essential: true
            });
          }
        }
      } else {
        // Only industry filter applied
        const response = await fetchCompaniesByIndustry(industry.sectorName);
        
        if (response.success && response.data) {
          const validCompanies = response.data.filter(company => 
            company.location?.coordinates?.length === 2 &&
            company.location.coordinates[0] !== 0
          );
          setCompanies(validCompanies);

          if (validCompanies.length > 0 && mapRef.current) {
            const bounds = new mapboxgl.LngLatBounds();
            validCompanies.forEach(company => {
              bounds.extend(company.location.coordinates);
            });
            mapRef.current.fitBounds(bounds, {
              padding: 100,
              maxZoom: 12,
              duration: 1500,
              essential: true
            });
          } else if (mapRef.current) {
            // Reset to default view if no companies found
            mapRef.current.flyTo({ 
              center: [79.0882, 21.1458], 
              zoom: 7,
              duration: 1500 
            });
          }
        }
      }
    } catch (err) {
      console.error("Industry filtering error:", err);
      setCompanies([]);
    } finally {
      setCompaniesLoading(false);
    }
  };

  // --- Load All Companies ---
  const loadAllCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const response = await fetchCompanies();
      if (response.success && response.data) {
        const validCompanies = response.data.filter(company => 
          company.location?.coordinates?.length === 2 &&
          company.location.coordinates[0] !== 0
        );
        setCompanies(validCompanies);
      }
    } catch (error) {
      console.error('Error loading all companies:', error);
    } finally {
      setCompaniesLoading(false);
    }
  };

  // --- Updated District Selection to Consider Industry Filter ---
  // const handleDistrictSelection = async (districtId) => {
  //   if (!districtId) {
  //     setSelectedDistrict(null);
  //     setSelectedDistrictDetails(null);
      
  //     // If industry is selected, show industry-filtered companies
  //     if (selectedIndustry) {
  //       handleIndustrySelection(selectedIndustry);
  //     } else {
  //       setCompanies([]);
  //       if (mapRef.current) {
  //         mapRef.current.flyTo({ 
  //           center: [79.0882, 21.1458],
  //           zoom: 7,
  //           duration: 1500 
  //         });
  //       }
  //     }
  //     return;
  //   }

  //   const district = districts.find(d => d.id === districtId);
  //   setSelectedDistrict(districtId);
  //   setSelectedDistrictDetails(district);
  //   setCompaniesLoading(true);

  //   try {
  //     const response = await fetchCompaniesByCity(district.name);
      
  //     if (response.success && response.data) {
  //       let validCompanies = response.data.filter(company => 
  //         company.location?.coordinates?.length === 2 &&
  //         company.location.coordinates[0] !== 0
  //       );

  //       // Apply industry filter if selected
  //       if (selectedIndustry) {
  //         const industry = industries.find(i => i._id === selectedIndustry);
  //         if (industry) {
  //           validCompanies = validCompanies.filter(company =>
  //             company.industry.toLowerCase().includes(industry.sectorName.toLowerCase())
  //           );
  //         }
  //       }

  //       setCompanies(validCompanies);

  //       if (validCompanies.length > 0 && mapRef.current) {
  //         const bounds = new mapboxgl.LngLatBounds();
  //         validCompanies.forEach(company => {
  //           bounds.extend(company.location.coordinates);
  //         });
          
  //         mapRef.current.fitBounds(bounds, {
  //           padding: 100,
  //           maxZoom: 12,
  //           duration: 1500,
  //           essential: true
  //         });
  //       } else if (mapRef.current) {
  //         mapRef.current.flyTo({ 
  //           center: [district.lng, district.lat], 
  //           zoom: 11,
  //           duration: 1500 
  //         });
  //       }
  //     }
  //   } catch (err) {
  //     console.error("Coordinate Mapping Error:", err);
  //     setCompanies([]);
  //   } finally {
  //     setCompaniesLoading(false);
  //   }
  // };

  // --- Render Interactive Markers ---
  useEffect(() => {
    if (!mapRef.current) return;
    
    // Clear existing markers before rendering new ones
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    companies.forEach(company => {
      if (!company.location?.coordinates) return;
      const [lng, lat] = company.location.coordinates;

      // Custom Marker UI
      const el = document.createElement('div');
      el.className = 'company-marker';
      el.style.cssText = `
        width: 48px; height: 48px; border-radius: 50%;
        border: 3px solid #3C007A; background: white;
        box-shadow: 0 4px 15px rgba(0,0,0,0.2); cursor: pointer;
        display: flex; align-items: center; justify-content: center;
        overflow: hidden;
      `;
const logoSrc = company?.logo
  ? `${process.env.REACT_APP_API_BASE_URL}/${company.logo.replace(/\\/g, "/").replace(/^\/+/, "")}`
  : "/fallback-logo.png";

el.innerHTML = `
  <img 
    src="${logoSrc}" 
    style="width:100%; height:100%; object-fit:cover;"
  />
`;

      // Interactive card-rendering logic for detailed information
     const popupHtml = `
  <div style="
    width: 280px; 
    background: #ffffff; 
    border-radius: 16px; 
    overflow: hidden; 
    font-family: 'Rubik', sans-serif; 
    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  ">
    <div style="
      height: 120px; 
      background-image: url('${company.banner ? `${process.env.REACT_APP_API_BASE_URL}/${company.banner.replace(/\\/g, '/').replace(/^\/+/, '')}` : 'https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=400'}');
      background-size: cover; 
      background-position: center;
      position: relative;
    ">
      <div style="
    position: absolute; 
    top: 10px; 
    right: 10px; 
    background: #f0fdf4; 
    color: #16a34a; 
    padding: 4px 8px; 
    border-radius: 6px; 
    font-size: 13px; 
    font-weight: 400; 
    display: flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #dcfce7;
    font-family: 'Rubik', sans-serif;
">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        <path d="m9 12 2 2 4-4"></path>
    </svg>
    Verified
</div>
    </div>

    <div style="
      width: 70px; 
      height: 70px; 
      background: #ffffff; 
      border-radius: 50%; 
      margin: -35px auto 0; 
      position: relative; 
      z-index: 2; 
      display: flex; 
      align-items: center; 
      justify-content: center;
      border: 4px solid #ffffff;
      box-shadow: 0 4px 10px rgba(0,0,0,0.08);
    ">
      <img src="${company.logo ? `${process.env.REACT_APP_API_BASE_URL}/${company.logo.replace(/\\/g, '/').replace(/^\/+/, '')}` : 'https://via.placeholder.com/60'}"
           style="width: 100%; height: 100%; border-radius: 50%; object-fit: cover;">
    </div>

    <div style="padding: 12px 20px 20px; text-align: center;">
      <h3 style="
        margin: 0; 
        color: #3C007A; 
        font-size: 17px; 
        font-weight: 400; 
        line-height: 1.2;
      ">
        ${company.companyName}
      </h3>
      <p style="margin: 4px 0 16px; color: #666666; font-size: 14px;">
        ${company.address?.city || 'Nagpur'}
      </p>

      <div style="display: grid; grid-template-columns: 85px 1fr; gap: 8px; text-align: left; margin-bottom: 20px;">
        <span style="color: #333333; font-size: 14px;">Industry</span>
        <span style="color: #666666; font-size: 13px; font-weight: 400;">${company.industry}</span>
        
        <span style="color: #333333; font-size: 14px;">Phone No.</span>
        <span style="color: #666666; font-size: 13px; font-weight: 400;">${company.phone || '+91 00000 00000'}</span>
        
        <span style="color: #333333; font-size: 14px;">Email</span>
        <span style="color: #666666; font-size: 13px; font-weight: 400; word-break: break-all;">${company.email || 'N/A'}</span>
      </div>

      <div style="
        display: flex; 
        align-items: center; 
        justify-content: space-between; 
        border-top: 1px solid #f0f0f0; 
        padding-top: 15px;
      ">
        <div style="display: flex; gap: 10px;">
           <div style="width: 32px; height: 32px; border-radius: 50%; background: #F3E8FF; display: flex; align-items: center; justify-content: center; cursor: pointer;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3C007A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
           </div>
           <div style="width: 32px; height: 32px; border-radius: 50%; background: #F3E8FF; display: flex; align-items: center; justify-content: center; cursor: pointer;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3C007A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
           </div>
        </div>
       <button style="
          padding: 8px 24px; 
          background: #ffffff; 
          border: 1px solid #3C007A; 
          color: #3C007A; 
          border-radius: 8px; 
          font-size: 14px; 
          fontWeight: 400; 
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Rubik', sans-serif;
        " 
        onmouseover="this.style.background='#3C007A'; this.style.color='#ffffff';" 
        onmouseout="this.style.background='#ffffff'; this.style.color='#3C007A';"
        onclick="window.open('/company/${company._id || company.id}', '_blank')">
          Details
        </button>
      </div>
    </div>
  </div>
`;

      const marker = new mapboxgl.Marker(el)
        .setLngLat([lng, lat])
        .setPopup(new mapboxgl.Popup({ offset: 30, closeButton: false }).setHTML(popupHtml))
        .addTo(mapRef.current);
      
      markersRef.current.push(marker);
    });
  }, [companies]);

  return (
    <div className="min-h-screen bg-gray-50 font-rubik" style={{marginTop: '20px'}}>
       <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '24px', marginBottom: '16px', padding: '10px' }}>
            
            {/* Left Section: Branding & Title */}
            <Space align="center" size={16}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Title 
                  level={2} 
                  style={{ 
                    margin: 0, 
                    color: "#3C007A", 
                    fontSize: "24px", 
                    fontWeight: 500,
                    lineHeight: 1.2
                  }}
                >
                  Industrial Highlight View
                </Title>
               
              </div>
            </Space>

            {/* Right Section: Interactive Controls */}
            <Space size={24} wrap>
              
              {/* Map Layer Switching Pill */}
              <div style={{
                background: '#F9FAFB',
                padding: '6px 14px',
                borderRadius: '16px',
                border: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                flexShrink: 0,
                maxWidth: '100%',
                overflowX: 'auto'
              }}>
                <Space size={8} style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                  <Layers size={20} style={{ color: '#3C007A' }} />
                  <Text style={{ fontSize: '15px', color: '#666666', fontWeight: 400, whiteSpace: 'nowrap' }}>Map Layers</Text>
                </Space>
                <Divider type="vertical" style={{ height: '24px', borderColor: '#D1D5DB', flexShrink: 0 }} />
                <Space size={6} style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {mapStyles.slice(0, 4).map(style => (
                    <Button
                      key={style.value}
                      type={mapStyle === style.value ? 'primary' : 'text'}
                      onClick={() => handleMapStyleChange(style.value)}
                      style={{
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: 400,
                        backgroundColor: mapStyle === style.value ? '#3C007A' : 'transparent',
                        color: mapStyle === style.value ? '#ffffff' : '#666666',
                        boxShadow: mapStyle === style.value ? '0 4px 10px rgba(60, 0, 122, 0.2)' : 'none'
                      }}
                    >
                      {style.label}
                    </Button>
                  ))}
                </Space>
              </div>

              {/* District Navigation */}
              <div style={{ position: 'relative' }}>
                <Select
                  showSearch
                  placeholder={
                    <Space>
                      {/* <MapPin size={20} style={{ color: '#FF8F00' }} /> */}
                      <span style={{ color: '#666666', fontSize: '16px' }}>Select District </span>
                    </Space>
                  }
                  value={selectedDistrict || undefined}
                  onChange={handleDistrictSelection}
                  suffixIcon={<ChevronDown size={20} style={{ color: '#3C007A' }} />}
                  style={{ 
                    minWidth: '340px', 
                  }}
                  className="custom-select-ui"
                  dropdownStyle={{ borderRadius: '12px', padding: '8px' }}
                >
                  {districts.map(d => (
                    <Select.Option key={d.id} value={d.id}>
                      <span style={{ fontWeight: 400, fontSize: '16px', color: '#333333' }}>{d.name}</span>
                    </Select.Option>
                  ))}
                </Select>
              </div>


                   <div style={{ position: 'relative' }}>
                <Select
                  showSearch
                  placeholder={
                    <Space>
                      {/* <MapPin size={20} style={{ color: '#FF8F00' }} /> */}
                      <span style={{ color: '#666666', fontSize: '16px' }}>Select Industry </span>
                    </Space>
                  }
                  value={selectedIndustry || undefined}
                  onChange={handleIndustrySelection}
                  suffixIcon={<ChevronDown size={20} style={{ color: '#3C007A' }} />}
                  style={{ 
                    minWidth: '340px', 
                  }}
                  className="custom-select-ui"
                  dropdownStyle={{ borderRadius: '12px', padding: '8px' }}
                >
                  {industries.map(ind => (
                    <Select.Option key={ind._id} value={ind._id}>
                      <span style={{ fontWeight: 400, fontSize: '16px', color: '#333333' }}>{ind.sectorName}</span>
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Space>
          </div>

      <div className="p-6">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden relative border border-gray-100" style={{ height: '700px' }}>
          {companiesLoading && (
            <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-20 flex flex-col items-center justify-center">
              <Loader className="animate-spin text-[#3C007A] mb-4" size={48} />
              <p className="font-semibold text-gray-600">Syncing Industry Markers...</p>
            </div>
          )}
          <div ref={mapContainerRef} className="absolute inset-0 w-full h-full" />
        </div>
      </div>

      <style>{`
        .mapboxgl-popup-content { 
          padding: 0 !important; 
          border-radius: 20px !important; 
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
        }
        .mapboxgl-popup-tip { display: none; }
        
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .district-marker:hover { animation: pulse 1s infinite; }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
};

export default VidharbhaIndustrialMap;